import * as THREE from 'three'
import { paramThreeToGL } from './utils'

/**
 * 将 Three.js 纹理对象适配为原生 WebGL 纹理，支持按版本增量上传。
 */
export class WebGLTexture {
  /** WebGL2 上下文 */
  gl: WebGL2RenderingContext
  /** 源 Three.js 纹理引用 */
  texture: THREE.Texture | THREE.DataTexture | THREE.CanvasTexture
  /** 底层 WebGLTexture 对象 */
  id: globalThis.WebGLTexture | null
  /** 绑定目标，固定 TEXTURE_2D */
  target: number
  /** 最近上传的源纹理版本号，用于差分 */
  version: number

  constructor(
    gl: WebGL2RenderingContext,
    texture: THREE.Texture | THREE.DataTexture | THREE.CanvasTexture
  ) {
    this.gl = gl

    this.texture = texture
    this.id = gl.createTexture()

    this.target = gl.TEXTURE_2D
    this.version = -1

    this.update(texture)
  }

  /**
   * 按源纹理版本号增量上传 GPU 数据。
   *
   * @param updatetexture - 兼容参数（当前未使用），内部以 this.texture 为准
   */
  update(_updatetexture?: THREE.Texture | THREE.DataTexture | THREE.CanvasTexture): void {
    if (!this.texture.image) {
      this.version = this.texture.version

      return
    }

    const gl = this.gl
    const texture = this.texture
    const image = texture.image as {
      width: number
      height: number
      data?: ArrayBufferView | null
    }

    if (this.version === texture.version) {
      return
    }

    this.target = gl.TEXTURE_2D

    gl.bindTexture(this.target, this.id)

    const level = 0
    const internalFormat = paramThreeToGL(gl, texture.format)
    const width = image.width
    const height = image.height
    const border = 0
    const srcFormat = internalFormat
    const srcType = paramThreeToGL(gl, texture.type)
    let data: any

    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, texture.flipY)
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, texture.premultiplyAlpha)
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, texture.unpackAlignment)

    if (texture instanceof THREE.DataTexture) {
      data = image.data

      gl.texParameteri(this.target, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
      gl.texParameteri(this.target, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)

      gl.texParameteri(this.target, gl.TEXTURE_MAG_FILTER, paramThreeToGL(gl, texture.magFilter))
      gl.texParameteri(this.target, gl.TEXTURE_MIN_FILTER, paramThreeToGL(gl, texture.minFilter))

      gl.texImage2D(
        this.target,
        level,
        internalFormat,
        width,
        height,
        border,
        srcFormat,
        srcType,
        data
      )
    } else if (texture instanceof THREE.CanvasTexture || texture instanceof THREE.Texture) {
      data = texture.image

      gl.texParameteri(this.target, gl.TEXTURE_WRAP_S, paramThreeToGL(gl, texture.wrapS))
      gl.texParameteri(this.target, gl.TEXTURE_WRAP_T, paramThreeToGL(gl, texture.wrapT))

      gl.texParameteri(this.target, gl.TEXTURE_MAG_FILTER, paramThreeToGL(gl, texture.magFilter))
      gl.texParameteri(this.target, gl.TEXTURE_MIN_FILTER, paramThreeToGL(gl, texture.minFilter))

      gl.texImage2D(this.target, level, internalFormat, internalFormat, srcType, data)

      if (texture instanceof THREE.Texture) {
        gl.generateMipmap(gl.TEXTURE_2D)
      }
    }

    gl.bindTexture(this.target, null)

    this.version = texture.version
  }
}
